import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';

// =============================================
// ASYNC THUNKS
// =============================================

// Fetch all system permissions
export const fetchPermissions = createAsyncThunk(
  'permissions/fetchPermissions',
  async ({ category, resource } = {}) => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (resource) params.append('resource', resource);
    const res = await axios.get(`/permissions?${params.toString()}`);
    return res.data;
  }
);

// Fetch permissions grouped by category
export const fetchPermissionsByCategory = createAsyncThunk(
  'permissions/fetchByCategory',
  async () => {
    const res = await axios.get('/permissions/categories');
    return res.data;
  }
);

// Fetch permission categories list
export const fetchPermissionCategories = createAsyncThunk(
  'permissions/fetchCategories',
  async () => {
    const res = await axios.get('/permissions/categories/list');
    return res.data;
  }
);

// Fetch current user's effective permissions
// Phase 3: Uses new JWT-based endpoint (0 DB queries when using JWT)
export const fetchMyPermissions = createAsyncThunk(
  'permissions/fetchMyPermissions',
  async (forceRefresh = false) => {
    // Use the new fast endpoint that reads from JWT first
    const endpoint = forceRefresh
      ? '/merchant/auth/permissions?refresh=true'
      : '/merchant/auth/permissions';
    const res = await axios.get(endpoint);
    return res.data;
  }
);

// Refresh JWT token with fresh permissions (after role change)
// Phase 3: Call this when a team member's role is updated
export const refreshPermissionsToken = createAsyncThunk(
  'permissions/refreshToken',
  async () => {
    const res = await axios.post('/merchant/auth/token/refresh');
    return res.data;
  }
);

// Fetch effective permissions for a specific team member
export const fetchMemberPermissions = createAsyncThunk(
  'permissions/fetchMemberPermissions',
  async (memberId) => {
    const res = await axios.get(`/permissions/member/${memberId}`);
    return res.data;
  }
);

// Check if current user has a specific permission
export const checkPermission = createAsyncThunk(
  'permissions/checkPermission',
  async (permissionCode) => {
    const res = await axios.get(`/permissions/check/${permissionCode}`);
    return { ...res.data, permissionCode };
  }
);

// Check multiple permissions
export const checkMultiplePermissions = createAsyncThunk(
  'permissions/checkMultiple',
  async ({ permissionCodes, requireAll = false }) => {
    const res = await axios.post('/permissions/check-multiple', {
      permissionCodes,
      requireAll
    });
    return res.data;
  }
);

// Seed system permissions (admin only)
export const seedPermissions = createAsyncThunk(
  'permissions/seed',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.post('/permissions/seed');
      return res.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to seed permissions' });
    }
  }
);

// =============================================
// SLICE
// =============================================

const permissionsSlice = createSlice({
  name: 'permissions',
  initialState: {
    // All available system permissions
    permissions: [],
    // Permissions grouped by category
    permissionsByCategory: {},
    // List of categories
    categories: [],
    // Current user's effective permissions
    myPermissions: {
      isOwner: false,
      role: null,
      roleName: null,
      permissionCodes: []
    },
    // Permission check results cache
    permissionChecks: {},
    // Status
    status: 'idle',
    myPermissionsStatus: 'idle',
    error: null
  },
  reducers: {
    clearPermissionError: (state) => {
      state.error = null;
    },
    clearPermissionChecks: (state) => {
      state.permissionChecks = {};
    },
    setPermissionCheck: (state, action) => {
      const { permissionCode, hasPermission } = action.payload;
      state.permissionChecks[permissionCode] = hasPermission;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all permissions
      .addCase(fetchPermissions.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPermissions.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.permissions = action.payload.permissions || [];
        state.error = null;
      })
      .addCase(fetchPermissions.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Fetch permissions by category
      .addCase(fetchPermissionsByCategory.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchPermissionsByCategory.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.permissionsByCategory = action.payload.permissions || {};
        state.categories = action.payload.categories || [];
        state.error = null;
      })
      .addCase(fetchPermissionsByCategory.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Fetch categories list
      .addCase(fetchPermissionCategories.fulfilled, (state, action) => {
        state.categories = action.payload.categories || [];
      })

      // Fetch my permissions
      .addCase(fetchMyPermissions.pending, (state) => {
        state.myPermissionsStatus = 'loading';
      })
      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        state.myPermissionsStatus = 'succeeded';
        state.myPermissions = {
          isOwner: action.payload.isOwner,
          role: action.payload.roleId,
          roleName: action.payload.roleName || action.payload.role,
          permissionCodes: action.payload.permissionCodes || [],
          permissions: action.payload.permissions || []
        };
        // Cache permission checks
        (action.payload.permissionCodes || []).forEach(code => {
          state.permissionChecks[code] = true;
        });
        state.error = null;
      })
      .addCase(fetchMyPermissions.rejected, (state, action) => {
        state.myPermissionsStatus = 'failed';
        state.error = action.error.message;
      })

      // Check single permission
      .addCase(checkPermission.fulfilled, (state, action) => {
        state.permissionChecks[action.payload.permissionCode] = action.payload.hasPermission;
      })

      // Check multiple permissions
      .addCase(checkMultiplePermissions.fulfilled, (state, action) => {
        if (action.payload.results) {
          Object.entries(action.payload.results).forEach(([code, has]) => {
            state.permissionChecks[code] = has;
          });
        }
      })

      // Seed permissions
      .addCase(seedPermissions.fulfilled, (state, action) => {
        // After seeding, we should refetch permissions
        state.status = 'idle';
      })

      // Phase 3: Refresh token with fresh permissions
      .addCase(refreshPermissionsToken.pending, (state) => {
        state.myPermissionsStatus = 'loading';
      })
      .addCase(refreshPermissionsToken.fulfilled, (state, action) => {
        state.myPermissionsStatus = 'succeeded';
        state.myPermissions = {
          isOwner: action.payload.isOwner,
          role: action.payload.roleId,
          roleName: action.payload.roleName,
          permissionCodes: action.payload.permissions || [],
          memberId: action.payload.memberId
        };
        // Update permission checks cache
        (action.payload.permissions || []).forEach(code => {
          state.permissionChecks[code] = true;
        });
        state.error = null;
      })
      .addCase(refreshPermissionsToken.rejected, (state, action) => {
        state.myPermissionsStatus = 'failed';
        state.error = action.error.message;
      });
  }
});

// =============================================
// SELECTORS
// =============================================

export const selectAllPermissions = (state) => state.permissions?.permissions || [];
export const selectPermissionsByCategory = (state) => state.permissions?.permissionsByCategory || {};
export const selectPermissionCategories = (state) => state.permissions?.categories || [];
export const selectMyPermissions = (state) => state.permissions?.myPermissions || { permissionCodes: [] };
export const selectIsOwner = (state) => state.permissions?.myPermissions?.isOwner || false;
export const selectPermissionsStatus = (state) => state.permissions?.status || 'idle';
export const selectPermissionsError = (state) => state.permissions?.error;

// Check if current user has a specific permission (from cache)
export const selectHasPermission = (permissionCode) => (state) => {
  const myPerms = state.permissions?.myPermissions;
  if (myPerms?.isOwner) return true;
  return myPerms?.permissionCodes?.includes(permissionCode) ||
         state.permissions?.permissionChecks?.[permissionCode] || false;
};

// Check if current user has any of the specified permissions
export const selectHasAnyPermission = (permissionCodes) => (state) => {
  const myPerms = state.permissions?.myPermissions;
  if (myPerms?.isOwner) return true;
  return permissionCodes.some(code =>
    myPerms?.permissionCodes?.includes(code) ||
    state.permissions?.permissionChecks?.[code]
  );
};

// Check if current user has all of the specified permissions
export const selectHasAllPermissions = (permissionCodes) => (state) => {
  const myPerms = state.permissions?.myPermissions;
  if (myPerms?.isOwner) return true;
  return permissionCodes.every(code =>
    myPerms?.permissionCodes?.includes(code) ||
    state.permissions?.permissionChecks?.[code]
  );
};

// Get permissions for a specific category
export const selectPermissionsForCategory = (category) => (state) => {
  return state.permissions?.permissionsByCategory?.[category] || [];
};

export const {
  clearPermissionError,
  clearPermissionChecks,
  setPermissionCheck
} = permissionsSlice.actions;

export default permissionsSlice.reducer;
